import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Loader2, Pencil, Trash2, X } from "lucide-react";
import React, { useState } from "react";
import { toast } from "sonner";

import * as api from "@/api/connections";
import { useAuth } from "@/auth/useAuth";
import { Button } from "@/components/ui/button";
import { extractErrorMessage } from "@/lib/api-errors";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { SsoBaseCard } from "@/pages/settings/cards/SsoBaseCard";
import type { OneCConnectionView } from "@/types/api";

type FormState = {
  name: string;
  base_url: string;
  username: string;
  password: string;
};

const EMPTY: FormState = { name: "", base_url: "", username: "", password: "" };

const FIELD_TITLES: Record<keyof FormState, string> = {
  name: "Название",
  base_url: "Адрес базы",
  username: "Логин 1С",
  password: "Пароль 1С",
};

export function Connection1CPage(): React.ReactElement {
  const qc = useQueryClient();
  // В SSO-сессии пароль 1С не используется нигде: портал ходит в 1С по JWT в
  // базы справочника. Создать/проверить/сменить адрес или пароль подключения
  // бэкенд тут отбивает 403 — такие кнопки и не показываем, а сверху — выбор
  // базы. Список подключений остаётся: они действуют при входе по паролю.
  const { user } = useAuth();
  const sso = user?.auth_mode === "sso";
  const { data: connections = [], isLoading } = useQuery({
    queryKey: ["connections"],
    queryFn: api.listConnections,
  });

  const [form, setForm] = useState<FormState>(EMPTY);
  const [editingId, setEditingId] = useState<string | null>(null);
  // Пустые поля после попытки отправки — для подсветки. Ошибка сервера
  // (проверка подключения не прошла) — видимой строкой у формы, не только
  // тостом: тост легко пропустить, а форму без причины «не создаётся» — нет.
  const [fieldErrors, setFieldErrors] = useState<Set<keyof FormState>>(new Set());
  const [formError, setFormError] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: api.createConnection,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["connections"] });
      setForm(EMPTY);
      setFormError(null);
      toast.success("Подключение проверено и создано");
    },
    onError: (e: { response?: { data?: { detail?: string } } }) => {
      const msg = extractErrorMessage(e, "Ошибка создания");
      setFormError(msg);
      toast.error(msg);
    },
  });

  const updateMut = useMutation({
    mutationFn: (args: { id: string; body: api.ConnectionUpdate }) =>
      api.updateConnection(args.id, args.body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["connections"] });
      setEditingId(null);
      setForm(EMPTY);
      setFormError(null);
      toast.success("Подключение обновлено");
    },
    onError: (e: { response?: { data?: { detail?: string } } }) => {
      const msg = extractErrorMessage(e, "Ошибка обновления");
      setFormError(msg);
      toast.error(msg);
    },
  });

  const deleteMut = useMutation({
    mutationFn: (args: { id: string; force?: boolean }) =>
      api.deleteConnection(args.id, args.force ?? false),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["connections"] });
      toast.success("Подключение удалено");
    },
    onError: (
      e: { response?: { status?: number; data?: { detail?: string } } },
      args,
    ) => {
      // Бэкенд отвечает 409 если есть зависимые маппинги «Договоры
      // комиссии». Подтверждаем и повторяем с force=true.
      if (e.response?.status === 409 && !args.force) {
        const msg = e.response?.data?.detail ?? "Есть зависимые маппинги. Удалить всё равно?";
        if (window.confirm(msg)) {
          deleteMut.mutate({ id: args.id, force: true });
        } else {
          // Явный feedback на отмену — иначе клик уходит в никуда.
          toast.message("Удаление отменено");
        }
        return;
      }
      toast.error(e.response?.data?.detail ?? "Ошибка удаления");
    },
  });

  const testMut = useMutation({
    mutationFn: api.testConnection,
    onSuccess: (res) => {
      // ok:false — штатный результат проверки (1С недоступна/ответила ошибкой).
      if (res.ok) toast.success(res.detail);
      else toast.error(res.detail);
    },
    onError: (e) =>
      //Брошенная ошибка = баг сервера (5xx/сеть/404), а не результат проверки —
      // без onError клик уходил в никуда и оператор думал, что кнопка сломана.
      toast.error(extractErrorMessage(e, "Не удалось проверить подключение")),
  });

  const setField = (key: keyof FormState, value: string) => {
    setForm((f) => ({ ...f, [key]: value }));
    // Начал заполнять — подсветку с поля снимаем сразу.
    if (fieldErrors.has(key)) {
      setFieldErrors((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Все поля обязательны (пароль при редактировании — «пусто = не менять»).
    const missing = new Set<keyof FormState>(
      (Object.keys(FIELD_TITLES) as (keyof FormState)[]).filter((k) => {
        if (k === "password" && editingId) return false;
        return !form[k].trim();
      }),
    );
    setFieldErrors(missing);
    if (missing.size > 0) {
      setFormError(
        "Заполните обязательные поля: " +
          [...missing].map((k) => FIELD_TITLES[k]).join(", "),
      );
      return;
    }
    setFormError(null);
    if (editingId) {
      const body: api.ConnectionUpdate = {
        name: form.name,
        base_url: form.base_url,
        username: form.username,
      };
      if (form.password) body.password = form.password;
      updateMut.mutate({ id: editingId, body });
    } else {
      createMut.mutate(form);
    }
  };

  const startEdit = (c: OneCConnectionView) => {
    setEditingId(c.id);
    setForm({ name: c.name, base_url: c.base_url, username: c.username, password: "" });
    setFieldErrors(new Set());
    setFormError(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY);
    setFieldErrors(new Set());
    setFormError(null);
  };

  const busy = createMut.isPending || updateMut.isPending;
  const errClass = (k: keyof FormState) =>
    cn(fieldErrors.has(k) && "border-destructive focus-visible:ring-destructive");

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Подключение к 1С УНФ</h1>
        {sso ? (
          <p className="text-sm text-muted-foreground">
            Вы вошли через SSO: в 1С портал ходит по токену под вашей учётной записью, в базу
            из справочника. Подключения с паролем ниже в SSO-сессии не используются — они
            действуют при входе по паролю или пропуску.
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">
            Введите базовый адрес 1С без постфиксов (например{" "}
            <code className="rounded bg-muted px-1">http://1c.example.com/unf</code>). Постфиксы{" "}
            <code className="rounded bg-muted px-1">/odata/standard.odata</code> и{" "}
            <code className="rounded bg-muted px-1">/hs/api-requests</code> подставляются
            автоматически.
          </p>
        )}
      </div>

      {sso ? (
        <SsoBaseCard />
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {editingId ? "Редактирование подключения" : "Новое подключение"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* noValidate: подсветка и текст ошибок свои, единообразные (браузерные
                пузыри required пропускают строку из пробелов и живут своей жизнью). */}
            <form onSubmit={onSubmit} className="space-y-3" noValidate>
              <div className="space-y-2">
                <Label htmlFor="name">Название *</Label>
                <Input
                  id="name"
                  placeholder="Основная база УНФ"
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                  className={errClass("name")}
                />
                {fieldErrors.has("name") && (
                  <p className="text-xs text-destructive">Обязательное поле</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="base_url">Адрес базы (без постфиксов) *</Label>
                <Input
                  id="base_url"
                  placeholder="http://1c.example.com/unf"
                  value={form.base_url}
                  onChange={(e) => setField("base_url", e.target.value)}
                  className={errClass("base_url")}
                />
                {fieldErrors.has("base_url") && (
                  <p className="text-xs text-destructive">Обязательное поле</p>
                )}
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="username">Логин 1С *</Label>
                  <Input
                    id="username"
                    autoComplete="off"
                    value={form.username}
                    onChange={(e) => setField("username", e.target.value)}
                    className={errClass("username")}
                  />
                  {fieldErrors.has("username") && (
                    <p className="text-xs text-destructive">Обязательное поле</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">
                    Пароль 1С {editingId ? (
                      <span className="text-xs">(оставьте пустым чтобы не менять)</span>
                    ) : (
                      "*"
                    )}
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="new-password"
                    value={form.password}
                    onChange={(e) => setField("password", e.target.value)}
                    className={errClass("password")}
                  />
                  {fieldErrors.has("password") && (
                    <p className="text-xs text-destructive">Обязательное поле</p>
                  )}
                </div>
              </div>
              {formError && (
                <p className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {formError}
                </p>
              )}
              <div className="flex items-center gap-2">
                <Button type="submit" disabled={busy}>
                  {busy && <Loader2 className="size-4 animate-spin" />}
                  {busy
                    ? "Проверяем подключение…"
                    : editingId
                      ? "Сохранить"
                      : "Проверить и создать"}
                </Button>
                {editingId && (
                  <Button type="button" variant="ghost" onClick={cancelEdit} disabled={busy}>
                    Отмена
                  </Button>
                )}
                {!busy && (
                  <span className="text-xs text-muted-foreground">
                    Сохранится только после успешной проверки связи с 1С.
                  </span>
                )}
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        <h2 className="text-lg font-semibold">Мои подключения</h2>
        {isLoading && <p className="text-sm text-muted-foreground">Загрузка…</p>}
        {!isLoading && connections.length === 0 && (
          <p className="text-sm text-muted-foreground">Подключений ещё нет.</p>
        )}
        <ul className="space-y-2">
          {connections.map((c) => (
            <li
              key={c.id}
              className="flex flex-col gap-2 rounded-md border p-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 font-medium">
                  {c.name}
                  {c.is_default && (
                    <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                      по умолчанию
                    </span>
                  )}
                </div>
                <div className="truncate text-xs text-muted-foreground">
                  {c.base_url} · {c.username}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {!sso && (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => testMut.mutate(c.id)}
                      disabled={testMut.isPending}
                    >
                      <Check className="size-4" /> Проверить
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => startEdit(c)}>
                      <Pencil className="size-4" />
                    </Button>
                  </>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    if (window.confirm(`Удалить подключение «${c.name}»?`)) {
                      deleteMut.mutate({ id: c.id });
                    }
                  }}
                >
                  <Trash2 className="size-4 text-destructive" />
                </Button>
                {!c.is_default && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      updateMut.mutate({ id: c.id, body: { is_default: true } })
                    }
                  >
                    <X className="size-4" /> Сделать основным
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
